import { useCallback, useState } from 'react';
import { useScrollTargetCommentId, useSelectionActions } from '../model/comments.store';

/**
 * Scrolls a comment's card into view once it's mounted in the DOM. Two sources
 * feed the pending id:
 *   - the returned `setPendingScrollId` for the local create flow ("scroll to
 *     the comment I just posted");
 *   - the store's `scrollTargetCommentId`, set by surfaces outside the list
 *     (e.g. the video annotation bar) and cleared here once handled.
 *
 * Consumers attach `registerRow(commentId)` as a callback ref on each rendered
 * comment row (or its wrapper) — either directly (`ref={registerRow(id)}`, see
 * `CommentsSidebar`) or nested inside another inline ref
 * (`registerRow(id)(el)`, see `BriefCommentsSidebar`).
 *
 * ## Identity semantics — do NOT memoize the inner callback
 *
 * `registerRow` itself is memoized on `[pendingScrollId, clearScrollTarget]`,
 * but the function it RETURNS is freshly allocated on every call. That is
 * deliberate and load-bearing, and it is stronger than "the identity changes
 * when the pending id changes":
 *
 *   - A row that is already mounted when the pending id arrives never
 *     re-mounts, so the only thing that can make React re-run the attach is a
 *     new ref identity. React detaches (calls the previous ref with `null`) and
 *     re-attaches on every render where the ref value differs, which gives the
 *     matching row its chance to scroll.
 *   - Because the inner closure is re-created per render, that re-attach also
 *     happens on renders where only the row list changed (a refetch reordering
 *     rows, replies expanding) — not just on pending-id changes.
 *   - `BriefCommentsSidebar` calls `registerRow(id)(el)` from inside its own
 *     inline ref, so it depends on that wrapper ref re-running rather than on
 *     `registerRow`'s own identity. An unstable inner callback is what keeps
 *     both attachment styles equivalent.
 *
 * Wrapping the inner function in `useCallback`/a per-id memo cache would pin
 * the ref identity and silently break "scroll to the comment I just posted":
 * the ref would not re-attach, the id/`commentId` comparison would never run
 * again, and nothing would fail in CI — the scroll would simply stop happening.
 * The attach itself is cheap (an early-return string compare), so the churn is
 * the intended trade.
 *
 * Mechanism: callback ref for a DOM side effect (no-use-effect skill,
 * "Callback refs for DOM side effects") — the scroll rides the node's attach
 * instead of a post-render effect keyed on the pending id.
 */
export function usePendingCommentScroll() {
  const [localScrollId, setLocalScrollId] = useState<string | null>(null);
  const scrollTargetId = useScrollTargetCommentId();
  const { clearScrollTarget } = useSelectionActions();

  const pendingScrollId = localScrollId ?? scrollTargetId;

  const registerRow = useCallback(
    (commentId: string) => (node: HTMLElement | null) => {
      if (!node) return;
      if (pendingScrollId !== commentId) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setLocalScrollId(null);
      clearScrollTarget();
    },
    [pendingScrollId, clearScrollTarget],
  );

  return { setPendingScrollId: setLocalScrollId, registerRow };
}
